import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue'

export function useCountdown(
  durationSeconds: Ref<number>,
  active: Ref<boolean>,
  onTimeout: () => void,
) {
  const remaining = ref(durationSeconds.value)
  const startedAt = ref<number | null>(null)
  let timerId: number | null = null
  let timedOut = false

  const progress = computed(() => {
    if (durationSeconds.value <= 0) return 0
    return Math.max(0, Math.min(1, remaining.value / durationSeconds.value))
  })

  function stop(): void {
    if (timerId !== null) {
      window.clearInterval(timerId)
      timerId = null
    }
  }

  function reset(): void {
    stop()
    timedOut = false
    remaining.value = durationSeconds.value
    startedAt.value = null
  }

  function start(): void {
    reset()
    if (!active.value || durationSeconds.value <= 0) return
    startedAt.value = performance.now()

    timerId = window.setInterval(() => {
      if (startedAt.value === null) return
      const elapsedSeconds = (performance.now() - startedAt.value) / 1000
      remaining.value = Math.max(0, Math.ceil(durationSeconds.value - elapsedSeconds))

      if (elapsedSeconds >= durationSeconds.value && !timedOut) {
        timedOut = true
        remaining.value = 0
        stop()
        onTimeout()
      }
    }, 100)
  }

  function elapsedMs(): number {
    if (startedAt.value === null) return 0
    return Math.max(0, Math.round(performance.now() - startedAt.value))
  }

  watch([durationSeconds, active], () => {
    if (active.value) start()
    else reset()
  }, { immediate: true })

  onBeforeUnmount(stop)

  return { remaining, progress, start, stop, reset, elapsedMs }
}
